import React, { useId, useState } from "react";
import classes from "../Management.module.scss";
import styles from "./DealerPipeline.module.scss";
import { daysUntil, formatDate } from "./dealerAdmin";

const SHOWN = 8;
// A sailing with no arrival date still counts this long after it left.
const LEFT_RECENTLY_DAYS = 30;
const SAILING_KEYS = ["pickupPort", "deliveryPort", "estimatedPickupDate", "estimatedArrivalDate"];

const shipKey = (name) => String(name || "").trim().replace(/\s+/g, " ").toUpperCase();

// Still under way, so another car can be on it: it hasn't arrived, or (with no
// arrival date) it left in the last few weeks.
const isCurrentSailing = (s) => {
  const arrives = daysUntil(s.estimatedArrivalDate);
  if (arrives !== null) return arrives >= 0;
  const departs = daysUntil(s.estimatedPickupDate);
  return departs !== null && departs >= -LEFT_RECENTLY_DAYS;
};

// The quote's "BALTIMORE" or "FREEPORT (TEXAS)" against whatever was typed as
// the sailing's arrival port ("Baltimore, MD").
const goesToPort = (s, quotePort) => {
  const port = String(quotePort || "").replace(/\(.*\)/, "").trim().toLowerCase();
  return !!port && s.deliveryPort.toLowerCase().includes(port);
};

// What a pick fills in: a sailing gives its ship, IMO, ports and dates; a ship
// known only from old sailings just its name and IMO. A blank on the other car
// leaves this car's value alone.
export const shipFieldsFrom = (row) => {
  const fields = { shipName: row.shipName, imo: row.imo };
  if (!row.shipOnly) SAILING_KEYS.forEach((key) => { fields[key] = row[key]; });
  return Object.fromEntries(Object.entries(fields).filter(([, value]) => value));
};

/**
 * The sailing most other cars to this car's quoted port are on, while it's
 * still under way: cars bought around the same time for the same port
 * usually share a ship.
 */
export const likelySailing = (sailings, quotePort) => (sailings || [])
  .filter((s) => isCurrentSailing(s) && goesToPort(s, quotePort))
  .sort((a, b) => b.cars - a.cars || b.estimatedPickupDate.localeCompare(a.estimatedPickupDate))[0] || null;

// The list under the field: sailings still under way that match what's typed,
// the quoted port's first, then by departure; then, once something is typed,
// ships seen only on older sailings.
const suggestionsFor = (sailings, typed, quotePort) => {
  const q = typed.trim().toLowerCase();
  const matching = sailings.filter((s) => s.shipName.toLowerCase().includes(q));
  const current = matching.filter(isCurrentSailing).sort((a, b) => (
    goesToPort(b, quotePort) - goesToPort(a, quotePort)
      || (a.estimatedPickupDate || "9999").localeCompare(b.estimatedPickupDate || "9999")
  ));
  if (!q) return current.slice(0, SHOWN);
  const sailing = new Set(current.map((s) => shipKey(s.shipName)));
  const ships = new Map();
  // Newest sailing first, so each ship shows when it last sailed.
  matching.forEach((s) => {
    const key = shipKey(s.shipName);
    if (!sailing.has(key) && !ships.has(key)) ships.set(key, { ...s, shipOnly: true });
  });
  return [...current, ...ships.values()].slice(0, SHOWN);
};

const route = (s) => {
  if (s.pickupPort && s.deliveryPort) return `${s.pickupPort} → ${s.deliveryPort}`;
  if (s.deliveryPort) return `to ${s.deliveryPort}`;
  return s.pickupPort ? `from ${s.pickupPort}` : "";
};

const describe = (row) => (row.shipOnly
  ? [
    row.estimatedPickupDate && `last sailed ${formatDate(row.estimatedPickupDate)}`,
    row.imo ? "fills the name and IMO" : "fills the name",
  ]
  : [
    route(row),
    row.estimatedPickupDate && `departs ${formatDate(row.estimatedPickupDate)}`,
    row.estimatedArrivalDate && `arrives ${formatDate(row.estimatedArrivalDate)}`,
    `${row.cars} other car${row.cars === 1 ? "" : "s"}`,
  ]).filter(Boolean).join(" · ");

/**
 * The Vessel name field, offering the ships other cars went on: on focus the
 * sailings still under way, and as staff type the ships whose name matches.
 * Picking one fills the rest of the shipping through onPick. A ship's IMO
 * never changes, so typing a known ship's name in full fills a blank IMO when
 * the field is left.
 *
 * Props: label, value, imo (the IMO as typed), sailings (from
 *        getDealerAdminSailings; null until loaded), quotePort,
 *        onChange(name), onPick(fields)
 */
const VesselNameField = ({ label, value, imo, sailings, quotePort, onChange, onPick }) => {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const rows = open && sailings ? suggestionsFor(sailings, value, quotePort) : [];

  const pick = (row) => {
    onPick(shipFieldsFrom(row));
    setOpen(false);
    setActive(-1);
  };

  const onKeyDown = (e) => {
    if (e.key === "Escape" && rows.length) {
      // Close the list, not the panel.
      e.stopPropagation();
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!rows.length) {
        setOpen(true);
        return;
      }
      const down = e.key === "ArrowDown";
      setActive((i) => (i < 0 ? (down ? 0 : rows.length - 1) : (i + (down ? 1 : -1) + rows.length) % rows.length));
      return;
    }
    if (e.key === "Enter" && rows[active]) {
      e.preventDefault();
      pick(rows[active]);
    }
  };

  const onBlur = () => {
    setOpen(false);
    setActive(-1);
    if (imo.trim() || !sailings || !value.trim()) return;
    const known = sailings.find((s) => s.imo && shipKey(s.shipName) === shipKey(value));
    if (known) onPick({ imo: known.imo });
  };

  return (
    <div className={classes.formGroup}>
      <label className={classes.formLabel} htmlFor={`${id}-input`}>{label}</label>
      <div className={styles.suggestField}>
        <input
          id={`${id}-input`}
          className={classes.formInput}
          value={value}
          placeholder={sailings && sailings.length ? "Type to find the ship other cars are on" : undefined}
          autoComplete="off"
          role="combobox"
          aria-autocomplete="list"
          aria-expanded={rows.length > 0}
          aria-controls={`${id}-list`}
          aria-activedescendant={rows[active] ? `${id}-option-${active}` : undefined}
          onChange={(e) => {
            onChange(e.target.value);
            setOpen(true);
            setActive(-1);
          }}
          onFocus={() => setOpen(true)}
          onBlur={onBlur}
          onKeyDown={onKeyDown}
        />
        {rows.length > 0 && (
          <ul id={`${id}-list`} role="listbox" className={styles.suggestList}>
            {rows.map((row, i) => (
              <li
                key={`${row.shipName}|${row.shipOnly ? "ship" : SAILING_KEYS.map((k) => row[k]).join("|")}`}
                id={`${id}-option-${i}`}
                role="option"
                aria-selected={i === active}
                className={`${styles.suggestOption} ${i === active ? styles.suggestActive : ""}`}
                // Keep focus in the field, so it doesn't blur before the pick.
                onMouseDown={(e) => {
                  e.preventDefault();
                  pick(row);
                }}
                onMouseEnter={() => setActive(i)}
              >
                <strong>{row.shipName}</strong>
                {row.imo && <span className={styles.suggestDetail}> · IMO {row.imo}</span>}
                <span className={`${styles.suggestDetail} ${styles.suggestLine}`}>{describe(row)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default VesselNameField;
